import React, { FC, useEffect, useRef } from 'react';
import { Route, RouteProps, Routes, useLocation, useMatch } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { OnAuthRequiredFunction } from '@okta/okta-react/bundles/types/OktaContext';
import { LoaderOverlay } from 'components';
import { internalAuth } from 'config/oktaConfig';
import { setAuthToken, useAutoRefresh, useDateChangeEffect, useSetAuthConfig, useSetAuthState, useSetIsAuthenticated } from 'state';

export const SecureRoute: FC<{
  onAuthRequired?: OnAuthRequiredFunction;
  errorComponent?: React.ComponentType<{ error: Error }>;
} & RouteProps & React.HTMLAttributes<HTMLDivElement>> = ({
  onAuthRequired,
  errorComponent,
  ...routeProps
}) => {
    const isLoginPending = useRef(false)
    const { oktaAuth, authState, _onAuthRequired } = useOktaAuth();
    const path = routeProps.path ? routeProps.path : ""
    const match = useMatch(path)
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const internal = queryParams.get('internal');
    const [handleLoginError, setHandleLoginError] = React.useState<Error | null>(null);
    const setAuthConfig = useSetAuthConfig();
    const setAuthState = useSetAuthState();
    const setIsAuthenticated = useSetIsAuthenticated();
    useAutoRefresh();
    useDateChangeEffect();

    useEffect(() => {

      const updateAuth = async () => {

        if (isLoginPending.current) {
          return;
        }

        if (authState && !authState.isAuthenticated) {
          if (internal !== null && oktaAuth.options.clientId !== internalAuth.clientId) {
            setAuthConfig(internalAuth);
          } else {
            isLoginPending.current = true;
            const originalUri = toRelativeUrl(window.location.href, window.location.origin);
            oktaAuth.setOriginalUri(originalUri);
            await oktaAuth.signInWithRedirect();
          }
        }
      }
      
      if (!match) {
        return;
      }

      if (!authState) {
        return;
      }

      if (authState.isAuthenticated) {
        isLoginPending.current = false;
        return;
      }

      if (!authState.isAuthenticated) {
        updateAuth().catch(err => {
          setHandleLoginError(err)
        })
      }

    }, [authState,
      oktaAuth,
      match,
      internal,
      isLoginPending,
      _onAuthRequired,
      setHandleLoginError
    ]);

    useEffect(() => {
      setAuthToken(authState?.accessToken?.accessToken);
      setAuthState(authState);
      setIsAuthenticated(authState?.isAuthenticated ?? false);
    }, [authState, setAuthState, setIsAuthenticated]);

    if (handleLoginError && errorComponent) {
      const ErrorComponent = errorComponent;
      return <ErrorComponent error={handleLoginError} />;
    }

    if (!authState || !authState?.isAuthenticated) {
      return <LoaderOverlay isLoading>Loading...</LoaderOverlay>;
    }

    return (
      <Routes>
        <Route {...routeProps} />
      </Routes>
    );
  }